import { useLocation } from "@tanstack/react-router"
import { Button } from "design-system/atoms/Button"
import { Label } from "design-system/atoms/Label"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "design-system/molecules/DropdownMenu"
import { cn } from "design-system/utils"
import { formatDate, isFilled, languages } from "lopermut"
import { Bot, BotOff, Ellipsis, MailCheck, MailX } from "lucide-react"
import { useIsAmphoraCompany } from "modules/customers/hooks/useIsAmphoraCompany"
import { useOptOutCustomerMutation } from "modules/customers/hooks/useOptOutCustomerMutation"
import { useRemoveOptOutCustomerMutation } from "modules/customers/hooks/useRemoveOptOutCustomerMutation"
import { useToggleCustomerAIMutation } from "modules/customers/hooks/useToggleCustomerAIMutation"
import { useCompanyClientsQuery } from "modules/customers/lib/companyClientsQuery"
import { formatCompanyClient } from "modules/customers/lib/formatCompanyClient"
import { useCustomerSentimentQuery } from "modules/insights/hooks/useCustomerSentimentQuery"
import { useCountries } from "modules/shared/hooks/useCountries"
import { RefObject, useEffect, useMemo, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { CustomerResponseDataSchema, GetSentimentResponseDataSchema } from "schema"
import { toast } from "sonner"
import { z } from "zod"
import { CustomerAIDisableDialog } from "./CustomerAIDisableDialog"
import { CustomerInfo } from "./CustomerInfo"
import { CustomerLogsTimeline } from "./CustomerLogsTimeline"
import { CustomerSentiment } from "./CustomerSentiment"
import { CustomerTags } from "./CustomerTags"
import { InstagramProfilePopover } from "./InstagramProfilePopover"
import { Metadata } from "./Metadata"
import { OptInDialog } from "./OptInDialog"
import { OptOutDialog } from "./OptOutDialog"
import { ShopifyProfile } from "./ShopifyProfile"
import { ShouldUseIntegration } from "./ShouldUseIntegration"
import { useCustomerForm } from "./useCustomerForm"

type CustomerInfoProps = {
  customer: z.infer<typeof CustomerResponseDataSchema>
  sentiment?: z.infer<typeof GetSentimentResponseDataSchema>
  isPending?: boolean
  isSentimentPending?: boolean
  actionsRef: RefObject<HTMLDivElement | null>
}

export function Profile({ customer, isPending, actionsRef }: CustomerInfoProps) {
  const { t } = useTranslation()
  const [isOptInDialogOpen, setIsOptInDialogOpen] = useState(false)
  const [isOptOutDialogOpen, setIsOptOutDialogOpen] = useState(false)
  const [isAIDisableDialogOpen, setIsAIDisableDialogOpen] = useState(false)
  const { data: sentiment, isPending: isSentimentPending } = useCustomerSentimentQuery(customer.id)
  const location = useLocation()
  const [actionsElement, setActionsElement] = useState<HTMLDivElement | null>(null)

  useEffect(() => {
    // Ref assignments happen after render; store in state to trigger re-render for portal target.
    setActionsElement(actionsRef.current)
  }, [actionsRef])

  const { formValues, updateFormValue, updateAndSave, save } = useCustomerForm({ customer })

  const { mutateAsync: removeOptOutCustomer, isPending: isOptInPending } =
    useRemoveOptOutCustomerMutation({
      customerId: customer.id,
    })
  const { mutateAsync: optOutCustomer, isPending: isOptOutPending } = useOptOutCustomerMutation({
    customerId: customer.id,
  })
  const isOptStatusPending = isOptInPending || isOptOutPending
  const { mutateAsync: toggleCustomerAI, isPending: isAITogglePending } =
    useToggleCustomerAIMutation(customer.id)

  const isAmphoraCompany = useIsAmphoraCompany()
  const { data: companyClients = [] } = useCompanyClientsQuery(isAmphoraCompany && customer.isFake)
  const companyClientOptions = companyClients.map((client) => ({
    label: formatCompanyClient(client) ?? client.externalId,
    value: client.id,
  }))
  const { options: countries } = useCountries()
  const instagramProfile = customer.instagramProfile?.username ? customer.instagramProfile : null

  const extraContent = useMemo(() => {
    const contentArray: React.ReactNode[] = []

    if (customer.optedOutAt) {
      const hrefCustomerInfo = customer.optedOutConversationId
        ? (() => {
            const params = new URLSearchParams()
            Object.entries(location.search as Record<string, unknown>).forEach(([key, value]) => {
              if (value != null) params.set(key, String(value))
            })
            params.set("conversationId", customer.optedOutConversationId.toString())
            params.set("tab", "conversations")
            return `${location.pathname}?${params.toString()}`
          })()
        : null

      contentArray.push(
        <CustomerInfo
          title={t("conversations.chat.customer.optedOutAt")}
          placeholder={t("conversations.chat.customer.optedOutAt")}
          value={formatDate(customer.optedOutAt, "P")}
          kind={hrefCustomerInfo ? "link" : "date"}
          href={hrefCustomerInfo}
          readOnly
        />,
      )
    }

    if (customer.denylistedAt) {
      contentArray.push(
        <CustomerInfo
          title={t("conversations.chat.customer.denylistedAt")}
          placeholder={t("conversations.chat.customer.denylistedAt")}
          value={formatDate(customer.denylistedAt, "P")}
          kind="date"
          readOnly
        />,
      )
    }

    if (contentArray.length > 0) {
      return <>{contentArray}</>
    }

    return null
  }, [
    customer.optedOutAt,
    customer.denylistedAt,
    customer.optedOutConversationId,
    location.search,
    location.pathname,
    t,
  ])

  const handleOptIn = async () => {
    await removeOptOutCustomer()
    setIsOptInDialogOpen(false)
  }

  const handleOptOut = async () => {
    await optOutCustomer()
    setIsOptOutDialogOpen(false)
  }

  const handleToggleAI = async (aiDisabled: boolean) => {
    await toggleCustomerAI({ aiDisabled })
    toast.success(
      t(
        aiDisabled ? "customers.profile.ai.disable.success" : "customers.profile.ai.enable.success",
      ),
    )
    setIsAIDisableDialogOpen(false)
  }

  function renderActions() {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="px-2" type="button">
            <Ellipsis className="size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {customer.aiDisabled ? (
            <DropdownMenuItem onClick={() => handleToggleAI(false)} disabled={isAITogglePending}>
              <Bot size={16} />
              <span>{t("customers.profile.ai.enable.action")}</span>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={() => setIsAIDisableDialogOpen(true)}
              disabled={isAITogglePending}
            >
              <BotOff size={16} />
              <span>{t("customers.profile.ai.disable.action")}</span>
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          {customer.optedOutAt ? (
            <DropdownMenuItem
              onClick={() => setIsOptInDialogOpen(true)}
              disabled={isOptStatusPending}
            >
              <MailCheck size={16} />
              <span>{t("customers.profile.messaging.removeOptOut")}</span>
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem
              onClick={() => setIsOptOutDialogOpen(true)}
              disabled={isOptStatusPending}
              className="text-destructive focus:text-destructive"
            >
              <MailX size={16} />
              <span>{t("customers.profile.messaging.markOptOut")}</span>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        save()
      }}
      className="flex flex-col w-full gap-4 py-4"
    >
      <OptInDialog
        open={isOptInDialogOpen}
        onClose={() => setIsOptInDialogOpen(false)}
        onConfirm={handleOptIn}
        isPending={isOptInPending}
      />
      <OptOutDialog
        open={isOptOutDialogOpen}
        onClose={() => setIsOptOutDialogOpen(false)}
        onConfirm={handleOptOut}
        isPending={isOptOutPending}
      />
      <CustomerAIDisableDialog
        open={isAIDisableDialogOpen}
        onClose={() => setIsAIDisableDialogOpen(false)}
        onConfirm={() => handleToggleAI(true)}
        isPending={isAITogglePending}
      />
      {actionsElement && createPortal(renderActions(), actionsElement)}
      <div
        className={cn(
          "grid grid-cols-1 gap-4 px-4",
          customer.shopifyCustomer && "lg:grid-cols-[minmax(0,1fr)_20rem]",
        )}
      >
        <section className="@container flex flex-col gap-2 rounded-xl border border-border px-4 py-3">
          <span className="pb-1 text-sm font-medium">{t("customers.profile.contact")}</span>
          <div className="grid grid-cols-1 gap-2 @3xl:grid-cols-2 @3xl:gap-x-8">
            {extraContent}
            <CustomerInfo
              title={t("customers.profile.firstName")}
              value={formValues.firstName}
              onChange={(value) => updateFormValue("firstName", value)}
              onBlur={save}
              placeholder={
                formValues.tentativeFirstName || t("customers.profile.firstNamePlaceholder")
              }
              kind="input"
              tooltip={
                !isFilled(formValues.firstName) && isFilled(formValues.tentativeFirstName)
                  ? t("customers.profile.tentativeNameTooltip")
                  : undefined
              }
            />
            <CustomerInfo
              title={t("customers.profile.lastName")}
              value={formValues.lastName}
              onChange={(value) => updateFormValue("lastName", value)}
              onBlur={save}
              placeholder={
                formValues.tentativeLastName || t("customers.profile.lastNamePlaceholder")
              }
              kind="input"
              tooltip={
                !isFilled(formValues.lastName) && isFilled(formValues.tentativeLastName)
                  ? t("customers.profile.tentativeNameTooltip")
                  : undefined
              }
            />
            <CustomerInfo
              title={t("customers.profile.phone")}
              value={formValues.phone}
              onChange={(value) => updateFormValue("phone", value)}
              onBlur={save}
              placeholder={formValues.tentativePhone || t("customers.profile.phonePlaceholder")}
              kind="input"
              tooltip={
                !isFilled(formValues.phone) && isFilled(formValues.tentativePhone)
                  ? t("customers.profile.tentativePhoneTooltip")
                  : undefined
              }
            />
            <CustomerInfo
              title={t("customers.profile.email")}
              value={formValues.email}
              onChange={(value) => updateFormValue("email", value)}
              onBlur={save}
              placeholder={formValues.tentativeEmail || t("customers.profile.emailPlaceholder")}
              kind="input"
              tooltip={
                !isFilled(formValues.email) && isFilled(formValues.tentativeEmail)
                  ? t("customers.profile.tentativeEmailTooltip")
                  : undefined
              }
            />
            {isAmphoraCompany && customer.isFake && (
              <CustomerInfo
                title={t("customers.profile.client")}
                value={formValues.clientId}
                onChange={(value) => updateAndSave("clientId", value)}
                placeholder={t("customers.profile.clientPlaceholder")}
                kind="select"
                options={companyClientOptions}
              />
            )}
            {isAmphoraCompany && !customer.isFake && (
              <CustomerInfo
                title={t("customers.profile.client")}
                readOnly
                value={formatCompanyClient(customer.client)}
                placeholder={t("customers.profile.clientPlaceholder")}
                kind="input"
              />
            )}
            <CustomerInfo
              title={t("customers.profile.preferredLanguage")}
              readOnly
              value={customer.preferredLocale}
              placeholder={t("customers.profile.languagePlaceholder")}
              kind="select"
              options={languages}
              tooltip={t("customers.profile.preferredLanguageTooltip")}
            />
            <CustomerInfo
              title={t("customers.profile.defaultLanguage")}
              value={formValues.locale}
              onChange={(value) => updateAndSave("locale", value)}
              placeholder={t("customers.profile.languagePlaceholder")}
              kind="select"
              options={languages}
              tooltip={t("customers.profile.defaultLanguageTooltip")}
            />
            <CustomerInfo
              title={t("customers.profile.country")}
              value={formValues.country}
              onChange={(value) => updateAndSave("country", value)}
              placeholder={t("customers.profile.countryPlaceholder")}
              kind="select"
              options={countries}
            />
            {instagramProfile && (
              <div className="grid grid-cols-[9rem_minmax(0,1fr)] items-center gap-3 min-h-7">
                <Label className="text-sm text-muted-foreground font-normal">
                  {t("customers.profile.instagramProfile")}
                </Label>
                <InstagramProfilePopover profile={instagramProfile} />
              </div>
            )}
            {customer.isFake && (
              <div className="col-span-full">
                <ShouldUseIntegration
                  value={formValues.useChannel}
                  onChange={(value) => updateAndSave("useChannel", value)}
                />
              </div>
            )}
            <div className="col-span-full">
              <CustomerTags
                tags={customer.tags ?? []}
                value={formValues.tags}
                onChange={(value) => updateAndSave("tags", value)}
              />
            </div>
            <Metadata
              className="col-span-full"
              metadata={formValues.metadata}
              onChange={(value) => updateFormValue("metadata", value)}
              onBlur={save}
            />
          </div>
        </section>
        {customer.shopifyCustomer && (
          <ShopifyProfile
            shopifyCustomer={customer.shopifyCustomer}
            tagNames={(customer.tags ?? [])
              .filter((tag) => tag.source === "Shopify")
              .map((tag) => tag.name)}
          />
        )}
      </div>
      <div className="flex flex-col gap-4 px-4">
        {sentiment && (
          <CustomerSentiment
            customer={customer}
            customerSentiment={sentiment}
            isPending={isPending}
            isSentimentPending={isSentimentPending}
          />
        )}
        <CustomerLogsTimeline customerId={customer.id} />
      </div>
    </form>
  )
}
